import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { createElement as _createElement } from "react";
import { DeleteOutlined, PlusOutlined, StopOutlined } from "@ant-design/icons";
import { Button, Checkbox, Col, Form, Input, Row, Select, Table, Tooltip } from "antd";
import { ContentBlockerField, EExtendsMergeStrategy, EUserRole, TemplateMetaDataField } from "@devowl-wp/api-real-cookie-banner";
import { Trans } from "@devowl-wp/react-frontend";
import { useStores } from "../../../../../../../store/root.js";
import { ColumnToolTipHeader } from "../../../../../../table/column-tooltip-header.js";
import { PermissionCheck } from "../../../../user/permission.js";
function useRuleColumns(t) {
    const { rcbAuthenticationStore } = useStores();
    const hasCreatorRole = rcbAuthenticationStore.hasRole(EUserRole.Creator);
    return [
        {
            title: /*#__PURE__*/ _jsx(ColumnToolTipHeader, {
                columnName: t("Expression"),
                tooltipTitle: t(`Expression can block any URL or HTML element in output of website. For examples and further explications, see the "Syntax and examples" link.`)
            }),
            dataIndex: "expression",
            key: "expression",
            render: (_, record, index)=>{
                return /*#__PURE__*/ _createElement(Form.Item, {
                    required: true,
                    ...record,
                    key: `expression-${index}`,
                    name: [
                        record.name,
                        "expression"
                    ],
                    rules: [
                        {
                            required: true,
                            type: "string",
                            message: "Please provide at least one URL or element to block."
                        }
                    ],
                    noStyle: true
                }, /*#__PURE__*/ _jsx(Input.TextArea, {
                    autoSize: {
                        minRows: 2,
                        maxRows: 5
                    },
                    disabled: !hasCreatorRole
                }));
            }
        },
        {
            title: t("Assigned rule groups"),
            dataIndex: "assignedToGroups",
            key: "assignedToGroups",
            render: (_, record, index)=>{
                return /*#__PURE__*/ _jsx(Form.Item, {
                    noStyle: true,
                    shouldUpdate: (prev, next)=>prev.ruleGroups !== next.ruleGroups || prev.extends?.identifier !== next.extends?.identifier || prev.extendedMergeStrategies != next.extendedMergeStrategies,
                    children: ({ getFieldValue })=>{
                        const ruleGroupEntries = useRuleGroupOptionsFromContentBlockerForm(getFieldValue);
                        const ruleGroupOptionIds = ruleGroupEntries?.map((group)=>{
                            if (group?.id?.trim().length > 0) {
                                return group.id;
                            }
                            return undefined;
                        }).filter(Boolean).filter((value, index, self)=>{
                            return self.indexOf(value) === index;
                        });
                        // now check if groups are assigned, but not available -> remove!
                        const rules = getFieldValue("rules");
                        rules?.map((rule)=>{
                            const ruleGroups = rule.assignedToGroups;
                            if (ruleGroups) {
                                rule.assignedToGroups = ruleGroups.filter((groupId)=>ruleGroupOptionIds.includes(groupId));
                            }
                        });
                        return /*#__PURE__*/ _jsx("div", {
                            children: ruleGroupOptionIds?.join("").trim().length > 0 ? /*#__PURE__*/ _jsx(Form.Item, {
                                name: [
                                    record.name,
                                    "assignedToGroups"
                                ],
                                noStyle: true,
                                children: /*#__PURE__*/ _jsx(Select, {
                                    disabled: !hasCreatorRole,
                                    mode: "multiple",
                                    allowClear: true,
                                    options: ruleGroupOptionIds?.map((groupId)=>{
                                        return {
                                            label: groupId,
                                            value: groupId
                                        };
                                    })
                                })
                            }) : /*#__PURE__*/ _jsx(Tooltip, {
                                title: t("No groups defined, yet. Please create a minimum of one rule group to have it available here."),
                                children: /*#__PURE__*/ _jsx(StopOutlined, {
                                    style: {
                                        width: "2rem"
                                    }
                                })
                            })
                        }, `ruleGroupOption-${record.key}`);
                    }
                }, `assignedToGroups-${index}`);
            }
        },
        {
            title: /*#__PURE__*/ _jsx(ColumnToolTipHeader, {
                columnName: t("Query args"),
                tooltipTitle: /*#__PURE__*/ _jsx(Trans, {
                    children: 'Use query arguments to identify parts like "?id=G-34953823" to target specific instances.'
                })
            }),
            dataIndex: "queryArgs",
            key: "queryArgs",
            render: (_, record, rowIndex)=>{
                const inFormStyle = {
                    style: {
                        margin: 0
                    },
                    labelCol: {
                        flex: "0 0 5rem"
                    },
                    colon: false
                };
                return /*#__PURE__*/ _jsx(Form.Item, {
                    noStyle: true,
                    children: /*#__PURE__*/ _jsx(Form.List, {
                        name: [
                            record.name,
                            "queryArgs"
                        ],
                        children: (fields, { add, remove })=>/*#__PURE__*/ _jsxs("div", {
                                children: [
                                    /*#__PURE__*/ _jsx(PermissionCheck, {
                                        minRole: EUserRole.Creator,
                                        children: /*#__PURE__*/ _jsx(Form.Item, {
                                            noStyle: true,
                                            style: {
                                                marginBottom: "1rem"
                                            },
                                            children: /*#__PURE__*/ _jsx(Button, {
                                                title: t("Add query arg"),
                                                icon: /*#__PURE__*/ _jsx(PlusOutlined, {}),
                                                size: "small",
                                                onClick: ()=>{
                                                    add();
                                                },
                                                style: {
                                                    marginLeft: "0.5rem"
                                                },
                                                children: t("Add")
                                            })
                                        })
                                    }),
                                    fields.map((field, index)=>/*#__PURE__*/ _jsxs(Row, {
                                            style: {
                                                boxShadow: "0 0px 3px rgba(0,0,0,0.2)",
                                                margin: "0.5rem",
                                                padding: "0.5rem"
                                            },
                                            children: [
                                                /*#__PURE__*/ _jsxs(Col, {
                                                    style: {
                                                        flex: 1
                                                    },
                                                    children: [
                                                        /*#__PURE__*/ _jsx(Form.Item, {
                                                            name: [
                                                                index,
                                                                "queryArg"
                                                            ],
                                                            label: t("Arg"),
                                                            ...inFormStyle,
                                                            required: true,
                                                            rules: [
                                                                {
                                                                    required: true,
                                                                    message: t("Please define query arg name.")
                                                                }
                                                            ],
                                                            children: /*#__PURE__*/ _jsx(Input, {
                                                                disabled: !hasCreatorRole
                                                            })
                                                        }),
                                                        /*#__PURE__*/ _jsx(Form.Item, {
                                                            name: [
                                                                index,
                                                                "regExp"
                                                            ],
                                                            label: t("Expression"),
                                                            ...inFormStyle,
                                                            required: true,
                                                            rules: [
                                                                {
                                                                    required: true,
                                                                    message: t("Please define expression for the query arg value.")
                                                                }
                                                            ],
                                                            children: /*#__PURE__*/ _jsx(Input, {
                                                                disabled: !hasCreatorRole
                                                            })
                                                        }),
                                                        /*#__PURE__*/ _jsx(Form.Item, {
                                                            name: [
                                                                index,
                                                                "isOptional"
                                                            ],
                                                            valuePropName: "checked",
                                                            label: t("Optional"),
                                                            ...inFormStyle,
                                                            children: /*#__PURE__*/ _jsx(Checkbox, {
                                                                disabled: !hasCreatorRole,
                                                                children: t("Yes")
                                                            })
                                                        })
                                                    ]
                                                }),
                                                /*#__PURE__*/ _jsx(PermissionCheck, {
                                                    minRole: EUserRole.Creator,
                                                    children: /*#__PURE__*/ _jsx(Col, {
                                                        children: /*#__PURE__*/ _jsx(Button, {
                                                            title: t("Remove query arg"),
                                                            icon: /*#__PURE__*/ _jsx(DeleteOutlined, {}),
                                                            size: "small",
                                                            onClick: ()=>remove(field.name),
                                                            style: {
                                                                marginLeft: "1px"
                                                            }
                                                        })
                                                    })
                                                })
                                            ]
                                        }, `query-arg-field-${rowIndex}-${index}`))
                                ]
                            }, `query-arg-fields-${rowIndex}`)
                    })
                }, `query-arg-${rowIndex}`);
            }
        },
        {
            title: /*#__PURE__*/ _jsx(ColumnToolTipHeader, {
                columnName: t("Source/Reference"),
                tooltipTitle: t("For better maintainability, the name and the URL of e.g. the plugin to which the rule belongs should be specified.")
            }),
            dataIndex: "source",
            key: "source",
            render: (_, record, index)=>{
                return /*#__PURE__*/ _createElement(Form.Item, {
                    ...record,
                    key: `source-ref-${index}`,
                    name: [
                        record.name,
                        "source"
                    ],
                    noStyle: true
                }, /*#__PURE__*/ _jsx(Input.TextArea, {
                    autoSize: {
                        minRows: 2,
                        maxRows: 5
                    },
                    disabled: !hasCreatorRole
                }));
            }
        },
        {
            title: /*#__PURE__*/ _jsx(ColumnToolTipHeader, {
                columnName: t("Required sibling rule"),
                tooltipTitle: t("The rule must be fulfilled together with a rule within its group, which does NOT have this flag, for the group to be resolved.")
            }),
            dataIndex: "needsRequiredSiblingRule",
            key: "needsRequiredSiblingRule",
            render: (_, record, index)=>{
                return /*#__PURE__*/ _createElement(Form.Item, {
                    ...record,
                    key: `needsRequiredSiblingRule-${index}`,
                    name: [
                        record.name,
                        "needsRequiredSiblingRule"
                    ],
                    valuePropName: "checked",
                    noStyle: true
                }, /*#__PURE__*/ _jsx(Checkbox, {
                    disabled: !hasCreatorRole,
                    children: t("Sibling rule required")
                }));
            },
            width: "11rem"
        }
    ];
}
function useRuleDisplayColumns(t) {
    return [
        {
            title: t("Expression"),
            dataIndex: "expression"
        },
        {
            title: t("Assigned rule groups"),
            dataIndex: "assignedToGroups",
            render: (groups)=>{
                if (groups === undefined) {
                    return "-";
                }
                return groups.join(",");
            }
        },
        {
            title: t("Query args"),
            dataIndex: "queryArgs",
            render: (queryArgs)=>{
                if (queryArgs === undefined || queryArgs.length === 0) {
                    return undefined;
                }
                return /*#__PURE__*/ _jsx(Table, {
                    size: "small",
                    pagination: false,
                    dataSource: queryArgs,
                    columns: [
                        {
                            title: t("Argument"),
                            dataIndex: "queryArg"
                        },
                        {
                            title: t("Expression"),
                            dataIndex: "regExp"
                        },
                        {
                            title: t("Optional"),
                            dataIndex: "isOptional",
                            render: (isOptional)=>{
                                return isOptional === undefined ? "-" : isOptional === true ? t("Yes") : t("No");
                            }
                        }
                    ]
                });
            }
        },
        {
            title: t("Source/Reference"),
            dataIndex: "source",
            render: (source)=>{
                return source ? source : "-";
            }
        },
        {
            title: t("Needs Required Sibling Rule"),
            dataIndex: "needsRequiredSiblingRule",
            render: (isRequiredSiblingRule)=>{
                if (isRequiredSiblingRule === undefined) {
                    return "-";
                }
                return Boolean(isRequiredSiblingRule) === true ? t("Yes") : t("No");
            }
        }
    ];
}
/**
 * Retrieves a list of options for a form select element based on the values of several form fields of content blocker template.
 *
 * @param getFieldValue A function that takes a `NamePath` argument and returns the value of the corresponding form field.
 * @returns An array of `IContentBlockerRuleGroup` objects representing the available options.
 */ function useRuleGroupOptionsFromContentBlockerForm(getFieldValue) {
    const ruleGroups = getFieldValue(ContentBlockerField.RuleGroups) || [];
    const extendedRuleGroups = getFieldValue(TemplateMetaDataField.Extends)?.ruleGroups;
    const extendedMergeStrategies = getFieldValue(TemplateMetaDataField.ExtendedMergeStrategies);
    let optionGroups = structuredClone(ruleGroups);
    // When extended handle options and strategy.
    if (extendedRuleGroups) {
        switch(extendedMergeStrategies.ruleGroups){
            case EExtendsMergeStrategy.Keep:
                // only use extended ones
                optionGroups = structuredClone(extendedRuleGroups);
                break;
            case EExtendsMergeStrategy.Append:
                // use both
                optionGroups = optionGroups.concat(...structuredClone(extendedRuleGroups));
                break;
            case EExtendsMergeStrategy.Overwrite:
                break;
            case EExtendsMergeStrategy.Clear:
                // none
                optionGroups = [];
                break;
        }
    }
    return optionGroups;
}
export { useRuleColumns, useRuleDisplayColumns };
